import { ensureAnonymousAuth } from "../auth/anonymousAuth";
import { resolveActiveFunctionUrl } from "../../configs/runtimeEnvironment";

const SAFE_ERROR_CODES = new Set([
  "invalid-request",
  "unauthenticated",
  "forbidden",
  "not-found",
  "conflict",
  "internal-error",
]);

const STATUS_ERROR_CODES = {
  400: "invalid-request",
  401: "unauthenticated",
  403: "forbidden",
  404: "not-found",
  409: "conflict",
};

function resolveUpdateCardTextUrl() {
  return resolveActiveFunctionUrl("updateLettuceTalkCardText", {
    devUrl: process.env.REACT_APP_UPDATE_LETTUCE_TALK_CARD_TEXT_URL_DEV,
    prodUrl: process.env.REACT_APP_UPDATE_LETTUCE_TALK_CARD_TEXT_URL_PROD,
    devFallbackUrls: [process.env.REACT_APP_CREATE_LETTUCE_TALK_CARDS_URL_DEV],
    prodFallbackUrls: [process.env.REACT_APP_CREATE_LETTUCE_TALK_CARDS_URL_PROD],
  });
}

function getSafeErrorCode(value, status) {
  if (typeof value === "string" && SAFE_ERROR_CODES.has(value)) {
    return value;
  }

  return STATUS_ERROR_CODES[status] || "internal-error";
}

function createUpdateError(code, status = 0) {
  const messageByCode = {
    "invalid-request": "We couldn't save that wording.",
    unauthenticated: "Please try again to save your wording.",
    forbidden: "You can't edit this card.",
    "not-found": "This card is no longer available.",
    conflict: "This card changed elsewhere. Please try again.",
    "internal-error": "We couldn't save your wording. Please try again.",
    "invalid-response": "We couldn't save your wording. Please try again.",
  };
  const error = new Error(
    messageByCode[code] || "We couldn't save your wording. Please try again."
  );
  error.code = code;
  error.status = status;
  return error;
}

function isValidTranslation(translation) {
  return (
    translation &&
    typeof translation === "object" &&
    typeof translation.text === "string" &&
    typeof translation.language === "string" &&
    typeof translation.languageCode === "string"
  );
}

function isValidResponse(data, cardId) {
  return (
    data &&
    typeof data === "object" &&
    data.cardId === cardId &&
    typeof data.questionRefined === "string" &&
    data.questionRefined.trim() &&
    Array.isArray(data.translations) &&
    data.translations.every(isValidTranslation) &&
    (data.translationStatus === "current" ||
      data.translationStatus === "needs_refresh") &&
    Number.isInteger(data.manualEditsCount) &&
    data.manualEditsCount >= 0 &&
    data.editApplied === true
  );
}

export async function updateLettuceTalkCardText({ cardId, editedText }) {
  if (
    typeof cardId !== "string" ||
    !cardId.trim() ||
    typeof editedText !== "string" ||
    !editedText.trim()
  ) {
    throw createUpdateError("invalid-request");
  }

  let url;

  try {
    url = resolveUpdateCardTextUrl();
  } catch (error) {
    throw createUpdateError("internal-error");
  }

  if (!url) {
    throw createUpdateError("internal-error");
  }

  let user;

  try {
    user = await ensureAnonymousAuth();
  } catch (error) {
    throw createUpdateError("unauthenticated");
  }

  let idToken;

  try {
    idToken = await user.getIdToken();
  } catch (error) {
    throw createUpdateError("unauthenticated");
  }

  let response;
  let data;

  try {
    response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        cardId: cardId.trim(),
        editedText,
      }),
      mode: "cors",
    });
    data = await response.json();
  } catch (error) {
    throw createUpdateError("internal-error");
  }

  if (!response.ok) {
    throw createUpdateError(
      getSafeErrorCode(data?.error || data?.code, response.status),
      response.status
    );
  }

  if (!isValidResponse(data, cardId.trim())) {
    throw createUpdateError("invalid-response", response.status);
  }

  return data;
}

export { isValidResponse as isValidUpdateCardTextResponse };
